/**
 * How the thread is divided into rows before it is drawn.
 *
 * Its own module, and types only, so it can be held to a test. Chat.tsx cannot
 * be imported outside a browser — it reaches Firebase through ../data, which
 * initialises an app on load — and this is the part of it that is pure and was
 * quietly wrong for it.
 */
import type { MessageDoc } from '../../shared/types';
import type { WithId } from './data';

export type Item = {
  kind: 'message';
  message: WithId<MessageDoc>;
  /** On a snap: the snap backs to it, and to them, oldest first. */
  thread?: WithId<MessageDoc>[];
  /**
   * A block of snaps on their own, side by side with nothing drawn between
   * them, in the order sent. Drawn as chips, one a person; `message` is the
   * first of them, for its place and its key.
   */
  block?: WithId<MessageDoc>[];
};

/**
 * Everything except the GiviSnap pointers.
 *
 * A pointer is the row that used to say somebody posted today's GiviSnap, and
 * chat is no longer where that gets announced: the GiviSnaps tab carries a dot
 * instead, which is the same news without a line in the conversation for every
 * person every day.
 *
 * The documents are still written and are not touched here. They are what the
 * push notification fires on, they are the record that it happened after the
 * photo behind it has expired, and chat is permanent — so they are filtered on
 * the way to the screen rather than removed from the thread.
 */
export function groupDailies(messages: WithId<MessageDoc>[]): Item[] {
  return messages
    .filter((message) => message.data.kind !== 'dailyPointer')
    .map((message) => ({ kind: 'message', message }));
}

const isSnap = (message: WithId<MessageDoc> | undefined) => message?.data.kind === 'snap';

/**
 * Snap backs, folded under the snap they answer.
 *
 * A run of snaps between two people was a run of rows, each one a bubble the
 * rest of the family had to scroll past to get back to what was being said.
 * Now the first snap carries the whole exchange in a thread, collapsed.
 *
 * A snap back to a snap back belongs to the same thread, so each is followed
 * up its replyTo to the first snap of the chain. Only through snaps: a snap
 * answering a text message is the start of a thread, not a reply in one, and
 * one whose snap is not loaded -- further back than the thread has paged --
 * stays where it is rather than vanishing into a row that is not there.
 *
 * The thread sits where its newest snap is, not its first. Left at the first,
 * a snap back sent today to a snap from last week would land a week up the
 * thread, where nobody reading from the bottom would ever find it.
 */
export function threadSnapBacks(items: Item[]): Item[] {
  const byId = new Map(items.map((item) => [item.message.id, item.message]));

  const rootOf = (message: WithId<MessageDoc>): string | null => {
    if (!isSnap(message)) return null;
    let current = message;
    const seen = new Set<string>();
    while (current.data.replyTo && !seen.has(current.id)) {
      seen.add(current.id);
      const parent = byId.get(current.data.replyTo);
      if (!isSnap(parent)) break;
      current = parent!;
    }
    return current.id === message.id ? null : current.id;
  };

  const threads = new Map<string, WithId<MessageDoc>[]>();
  const lastIndex = new Map<string, number>();
  items.forEach((item, index) => {
    const root = rootOf(item.message);
    if (!root) return;
    threads.set(root, [...(threads.get(root) ?? []), item.message]);
    lastIndex.set(root, index);
  });
  if (!threads.size) return items;

  // Each root is drawn at the place of its newest reply, and nowhere else.
  const rootAt = new Map([...lastIndex].map(([root, index]) => [index, root]));
  const out: Item[] = [];
  items.forEach((item, index) => {
    const root = rootAt.get(index);
    if (root) {
      out.push({ kind: 'message', message: byId.get(root)!, thread: threads.get(root) });
      return;
    }
    if (threads.has(item.message.id) || rootOf(item.message)) return;
    out.push(item);
  });
  return out;
}

/** The newest message an item holds: its block's or thread's last, or itself. */
export function latestOf(item: Item): WithId<MessageDoc> {
  if (item.block) return item.block[item.block.length - 1];
  return item.thread?.[item.thread.length - 1] ?? item.message;
}

/** Everything an item holds, the row itself first. */
export function messagesOf(item: Item): WithId<MessageDoc>[] {
  if (item.block) return item.block;
  return [item.message, ...(item.thread ?? [])];
}

/** A snap drawn on its own: not an exchange, not deleted. */
const isSingleSnap = (item: Item) =>
  item.message.data.kind === 'snap' && !item.thread && !item.block && !item.message.data.deletedAt;

/**
 * Snaps on their own, side by side, gathered into blocks.
 *
 * A block is every single snap between two things the thread draws -- a
 * message, a photo, an exchange -- so it sits exactly where those snaps
 * happened and the chat keeps its order; the next message starts a fresh one.
 * An exchange ends a block too: it is drawn where its newest snap back is,
 * and a block reaching past it would put older snaps below a newer one.
 *
 * One snap on its own is a block of one: Carter wants the chip every time.
 */
export function blockSnaps(items: Item[]): Item[] {
  const out: Item[] = [];
  let run: Item[] = [];
  const flush = () => {
    if (run.length > 0) {
      out.push({ kind: 'message', message: run[0].message, block: run.map((each) => each.message) });
    }
    run = [];
  };
  for (const item of items) {
    if (isSingleSnap(item)) {
      run.push(item);
    } else {
      flush();
      out.push(item);
    }
  }
  flush();
  return out;
}
